/**
 * PORTED — RO-11-03 / #44588.
 * Origin: `excellent/app/_chrome/nav-pulse.tsx` (no `next/*`; pure client + useApi).
 *
 * Copied into `client/shell/chrome/` so ModeRail never imports from `app/`
 * (PRD D6 — both stacks run side by side until L10 deletes the Next tree).
 */

import { useMemo } from "react";
import { IntentDot, band, cn, useHydrated, type PulseBand as Band, type Intent } from "@excellent/ui";
import { useApi } from "@excellent/ui/api";
import { pulse, type PulseAggregateResponse } from "@excellent/ui/api/contracts/pulse";
import { pulseTelemetry, type TelemetryNavEntryDTO } from "@excellent/ui/api/contracts/pulse-telemetry";

/**
 * Nav-Pulse — the Pulse, read straight off the left mode rail.
 *
 * Folds the Pulse's health language onto the navigation so the rail itself says
 * "what's going on where" without a panel: a **micro-sparkline** trails each mode
 * row (the mode's section-health shape), and a **status dot** trails each expanded
 * sub-view (its section's health). Both speak the canonical band tones
 * (ok · watch · risk → success · warning · danger).
 *
 * DATA — free + fresh. Each mode reads the BARE per-mode wall key
 * (`/api/pulse/<mode>`, no `rollup`) via `useApi`. That is the SAME key the
 * always-mounted `PulsePreloader` warms and the SWR provider persists to
 * localStorage, so the rail's reads dedupe against the warm cache (net-zero
 * fetches, instant on navigation) and stay fresh on focus-revalidate — no new
 * server work, no polling, and no `fetch()` (the no-fetch rail rule holds). The
 * bare wall's `groups` already carry each section's `health` + `domain.area`,
 * which is everything both decorations need. Workspace-tier health (the rail is
 * global orientation chrome); project-scoped health stays on the project's Pulse.
 *
 * CALM — only surface problems. The sub-view dot renders ONLY for watch/risk, so a
 * healthy rail stays silent; the sparkline shows only instrumented (live) sections,
 * so an un-measured mode shows nothing at all.
 */

// ── band tones (mirrors pulse-wall §0 / granular-pulse; canonical thresholds live
//    in @excellent/core health-rules, re-derived locally to keep this a pure,
//    server-free client module like its siblings) ──────────────────────────────
/** The solid health tones. Every rendered bar carries its band color
 *  full-strength — active or not — so the sparkline reads its health at a glance
 *  (Matt 2026-07-20: "the color of the bar, not a general color"). */
const BAND_FILL: Record<Band, string> = {
  ok: "bg-success",
  watch: "bg-warning",
  risk: "bg-danger",
};
const BAND_INTENT: Record<Band, Intent> = {
  ok: "success",
  watch: "warning",
  risk: "danger",
};

/** One section of a mode's wall, reduced to what the rail draws. */
export interface NavSection {
  /** The section's domain area slug — the join key to a sub-view's `?area=`. */
  area: string;
  key: string;
  health: number | null;
  /** Representative target (mean of the section's live touch targets) for banding. */
  target: number;
}

export interface ModeWall {
  overall: number | null;
  sections: NavSection[];
}

/** Collapse a bare mode-wall response to the rail's `NavSection[]`. Mirrors
 *  granular-pulse: keep groups with live touches; representative target = mean of
 *  the group's live touch targets (fallback 70), so banding matches the home. */
function wallSections(data: PulseAggregateResponse | undefined): NavSection[] {
  return (data?.groups ?? [])
    .filter((g) => (g.touches?.length ?? 0) > 0)
    .map((g) => {
      const liveTargets = g.touches
        .filter((t) => t.health != null)
        .map((t) => t.target);
      const target = liveTargets.length
        ? Math.round(liveTargets.reduce((a, t) => a + t, 0) / liveTargets.length)
        : 70;
      return {
        area: g.domain?.area ?? g.key,
        key: g.key,
        health: g.health,
        target,
      };
    });
}

/**
 * Subscribe to one mode's BARE wall (or nothing when `scope` is null — Pulse off).
 * Rides the preloaded/persisted cache; `keepPreviousData` so a revalidate never
 * flashes the rail back to empty.
 */
export function useModeWall(scope: string | null): ModeWall {
  const { data } = useApi(
    pulse.aggregate,
    // Empty query ⇒ the BARE key `/api/pulse/<scope>` — byte-identical to the key
    // PulsePreloader warms + the SWR provider persists, so this dedupes against the
    // warm cache (net-zero fetches) instead of forking a cold `?rollup=…` key.
    scope ? { params: { scope }, query: {} } : null,
    { keepPreviousData: true },
  );
  // That persisted cache is seeded from localStorage on the client's first render,
  // so the rail would paint sparklines the server (empty cache) didn't — a
  // hydration mismatch (the sub-view dot / sparkline render `null` when empty).
  // Hold the empty wall until hydrated so render #0 matches the server; the warm
  // data reveals on the next render, and client navigations stay instant.
  const hydrated = useHydrated();
  return useMemo(
    () => ({
      overall: hydrated ? data?.overall ?? null : null,
      sections: hydrated ? wallSections(data) : [],
    }),
    [data, hydrated],
  );
}

/** Extract a sub-view's section area from its href: the `?area=` slug, else the
 *  route tail (route-owning domains like `/agents/runs` → `runs`). */
function hrefArea(href: string): string | null {
  const qi = href.indexOf("?");
  if (qi >= 0) {
    const area = new URLSearchParams(href.slice(qi + 1)).get("area");
    if (area) return area;
  }
  const path = (qi >= 0 ? href.slice(0, qi) : href).replace(/\/+$/, "");
  const segs = path.split("/").filter(Boolean);
  return segs.length >= 2 ? (segs[segs.length - 1] ?? null) : null;
}

/**
 * The mode-row micro-sparkline — one tone bar per LIVE section, the mode's health
 * shape. Renders nothing when the mode has no instrumented section (calm).
 *
 * Fixed-width, right-anchored: every sparkline reserves the SAME track width and
 * `justify-end`s its bars, so the rightmost bar of every mode lands on one clean
 * vertical line — the column reads even regardless of how many sections a mode
 * has. (Un-instrumented sections are simply dropped, not drawn as ghost ticks,
 * which is what made the colored bars end at different x.) Every bar carries its
 * own band color full-strength (active or not); bars are decorative — the
 * wrapper carries an aria-label with the rollup number.
 */
export function NavSparkline({
  sections,
  label,
  overall,
}: {
  sections: NavSection[];
  label: string;
  overall: number | null;
}) {
  const live = sections.filter((s) => s.health != null);
  if (live.length === 0) return null;
  return (
    <span
      className="ml-auto flex h-3.5 w-8 shrink-0 items-end justify-end gap-0.5"
      role={overall != null ? "img" : undefined}
      aria-label={overall != null ? `${label} health ${overall} of 100` : undefined}
    >
      {live.map((s, i) => {
        const b = band(s.health!, s.target);
        return (
          <span
            key={s.key + i}
            aria-hidden
            // Always the band color — a health bar is never rendered neutral.
            className={cn("w-[3px] shrink-0 rounded-xs", BAND_FILL[b])}
            style={{ height: `${Math.max(s.health!, 14)}%` }}
          />
        );
      })}
    </span>
  );
}

/**
 * The sub-view status dot — the section's health for one expanded sub-nav row.
 * Joins the row's section area to the mode's sections, aggregates (mean of live),
 * and shows a dot ONLY for watch/risk (the calm default: healthy stays silent). No
 * match / no live data / ok ⇒ nothing.
 *
 * The join key is the explicit `area` when the sub-nav row carries one (route-owning
 * domains whose route tail diverges from the pulse area slug — Agents ▸ Workflows at
 * `/agents/workflows` is pulse area `fleet`), else the area derived from the href
 * (`?area=` URLs and route tails that already match). Deriving from the tail alone
 * silently dropped the dot on the diverging rows — the bug this `area` prop closes.
 */
export function SubViewDot({
  sections,
  href,
  area: areaOverride,
}: {
  sections: NavSection[];
  href: string;
  /** Explicit pulse area slug for this row — preferred over the href-derived tail. */
  area?: string;
}) {
  const area = areaOverride ?? hrefArea(href);
  if (!area) return null;
  const live = sections.filter((s) => s.area === area && s.health != null);
  if (live.length === 0) return null;
  const health = Math.round(
    live.reduce((a, s) => a + (s.health ?? 0), 0) / live.length,
  );
  const target = Math.round(live.reduce((a, s) => a + s.target, 0) / live.length);
  const k = band(health, target);
  if (k === "ok") return null;
  return (
    <IntentDot
      intent={BAND_INTENT[k]}
      size="xs"
      className="ml-auto shrink-0"
      title={`${area} health ${health} of 100`}
    />
  );
}

// ══ Dynamic plane (docs/pulse/DYNAMIC-PULSE-INTEGRATION-PRD.md §4 P6) ═════════
// The mode-row decoration's DATA source when `pulse.dynamic.enabled` is on — the
// learned-baseline telemetry plane, not the classic wall above. Same visual
// language (the bars, the geometry/sizing): a micro-sparkline per mode row, one
// bar per day of the mode's elected ANCHOR series (its output rate, Pivotal
// velocity), tinted by the section's worst SURFACED finding — not per-group
// health bands, because dynamic sections don't have groups, they have one
// anchor + deviations from it. A mode with no served section (product,
// personal) gets no entry and the sparkline renders nothing — the ghost/absent
// treatment, never a fall-back to the classic wall (`useModeWall` above).

/** Mirrors the dynamic gutter rail's `KIND_FILL` (packages/ui/src/blocks/pulse/
 *  pulse-gutter-rail.tsx — a local, non-exported const there, read-only
 *  reference for this work): a high regression is alarming, a medium one
 *  cautionary, a shift informational. No surfaced findings (or only good news,
 *  `improvement`) mirrors the classic wall's "ok" band — quiet is healthy, so
 *  the row reads a definitive success color, not a blank one. */
function navTone(worstKind: TelemetryNavEntryDTO["surfaced"]["worstKind"]): string {
  switch (worstKind) {
    case "regression-high":
      return "bg-danger";
    case "regression-medium":
      return "bg-warning";
    case "shift":
      return "bg-info";
    default:
      return "bg-success";
  }
}

/**
 * Subscribe ONCE to the nav telemetry op — or nothing when `enabled` is false
 * (dynamic off, or Pulse off entirely). This is the SINGLE fetch that serves
 * every mode row's decoration; the chrome must never fan out one request per
 * mode the way the classic wall's per-mode `useModeWall` does. Same hydration
 * guard as `useModeWall` (the server render always sees an empty map).
 */
export function useNavTelemetry(enabled: boolean): Map<string, TelemetryNavEntryDTO> {
  const { data } = useApi(pulseTelemetry.nav, enabled ? {} : null, { keepPreviousData: true });
  const hydrated = useHydrated();
  return useMemo(() => {
    if (!hydrated || !data) return new Map<string, TelemetryNavEntryDTO>();
    return new Map(data.entries.map((e) => [e.section, e]));
  }, [data, hydrated]);
}

/** A day renders as a bar only when its anchor value reaches this share of the
 *  mode's peak. Below it, the bar could only draw at the featureless visibility
 *  floor — a "blank" stub indistinguishable from a zero day — so it's dropped.
 *  Tying the alive-threshold to the floor means every rendered bar rises visibly
 *  above it: the sparkline shows the mode's PEAKS, never a row of flat stubs
 *  (Matt 2026-07-20: "only show the bars that are alive"). */
const PEAK_FLOOR_PCT = 14;

/**
 * The DYNAMIC mode-row micro-sparkline — same geometry as `NavSparkline`
 * (fixed track, right-anchored, 3px bars, 2px gap): one bar per ALIVE day of the
 * mode's anchor `recentDaily` (days reaching `PEAK_FLOOR_PCT` of the mode's
 * peak — quiet/zero days are dropped, not floored to a blank stub), height
 * scaled to that peak (a trend shape, not a health percentage). Every bar
 * carries the section's tone full-strength. Renders nothing when the mode has no
 * nav entry, no elected anchor, or no alive day — the calm/ghost default for an
 * un-instrumented, unserved, or idle mode (e.g. Customers with no pipeline this
 * week shows nothing at all).
 */
export function NavSparklineDynamic({
  entry,
  label,
}: {
  /** This mode's nav telemetry entry — undefined when no stream serves it
   *  (product, personal) or the fetch hasn't landed yet. */
  entry: TelemetryNavEntryDTO | undefined;
  label: string;
}) {
  const daily = entry?.anchor?.recentDaily ?? [];
  const peak = Math.max(0, ...daily);
  // Only the days that clear the visibility floor relative to the peak — the
  // alive peaks. An all-zero (or entirely sub-floor) window yields nothing.
  const bars = peak > 0 ? daily.filter((v) => (v / peak) * 100 >= PEAK_FLOOR_PCT) : [];
  if (bars.length === 0) return null;
  const fill = navTone(entry?.surfaced.worstKind ?? null);
  const count = entry?.surfaced.count ?? 0;
  return (
    <span
      className="ml-auto flex h-3.5 w-8 shrink-0 items-end justify-end gap-0.5"
      role="img"
      aria-label={`${label}: ${count} surfaced finding${count === 1 ? "" : "s"}`}
    >
      {bars.map((v, i) => (
        <span
          key={i}
          aria-hidden
          // Always the section tone — never a neutral/blank bar. Survivors clear
          // PEAK_FLOOR_PCT, so their scaled height needs no separate floor.
          className={cn("w-[3px] shrink-0 rounded-xs", fill)}
          style={{ height: `${(v / peak) * 100}%` }}
        />
      ))}
    </span>
  );
}

/** A problem finding-tone → `IntentDot` intent. `improvement` (good news) is
 *  deliberately ABSENT: an improvement is not a warning, so it reads calm (no
 *  dot), mirroring the classic dot's "only watch/risk surfaces" rule. */
const NAV_TONE_INTENT: Partial<
  Record<TelemetryNavEntryDTO["areas"][number]["worstKind"], Intent>
> = {
  "regression-high": "danger",
  "regression-medium": "warning",
  shift: "info",
};

/**
 * The DYNAMIC sub-view status dot — the telemetry-plane counterpart to
 * `SubViewDot`. Joins the sub-nav row's area (the explicit `area`, else the tail
 * derived from the href) against the mode's per-area finding fold (`entry.areas`,
 * one entry per sub-nav row with ≥1 surfaced finding) and shows a dot ONLY for a
 * problem tone — a regression (high → danger, medium → warning) or a shift
 * (info). An `improvement`, no matching area, or no entry ⇒ nothing (the calm
 * default: a good-news improvement is not a warning, so a healthy rail stays
 * silent, exactly like the classic dot).
 */
export function SubViewDotDynamic({
  entry,
  href,
  area: areaOverride,
}: {
  /** This mode's nav telemetry entry — undefined when no stream serves it or the
   *  fetch hasn't landed yet. */
  entry: TelemetryNavEntryDTO | undefined;
  href: string;
  /** Explicit pulse area slug for this row — preferred over the href-derived tail
   *  (route-owning rows whose tail ≠ pulse area, e.g. Workflows → `fleet`). */
  area?: string;
}) {
  const area = areaOverride ?? hrefArea(href);
  if (!area) return null;
  const fold = entry?.areas.find((a) => a.area === area);
  if (!fold) return null;
  const intent = NAV_TONE_INTENT[fold.worstKind];
  if (!intent) return null;
  const { count } = fold;
  return (
    <IntentDot
      intent={intent}
      size="xs"
      className="ml-auto shrink-0"
      title={`${count} surfaced finding${count === 1 ? "" : "s"}`}
    />
  );
}
